import React, { useState, useEffect } from 'react';
import { DollarSign, Save, RefreshCw, ChevronLeft, AlertCircle, Power, Calendar, FileText, KeyRound, MailCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AdminOrgInvites from '../components/AdminOrgInvites';

const AdminSettings = () => {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Monetization toggle state
  const [monetization, setMonetization] = useState(null);
  const [togglingMonetization, setTogglingMonetization] = useState(false);
  const [monetizationMessage, setMonetizationMessage] = useState(null);

  // Site config state (copyright year, company name, tagline)
  const [siteConfig, setSiteConfig] = useState(null);
  const [copyrightYear, setCopyrightYear] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [tagline, setTagline] = useState('');
  const [savingSite, setSavingSite] = useState(false);
  const [siteMessage, setSiteMessage] = useState(null);

  // Legal docs state
  const [legal, setLegal] = useState(null);
  const [activeLegalTab, setActiveLegalTab] = useState('privacy');
  const [legalTitle, setLegalTitle] = useState('');
  const [legalContent, setLegalContent] = useState('');
  const [legalEffectiveDate, setLegalEffectiveDate] = useState('');
  const [savingLegal, setSavingLegal] = useState(false);
  const [legalMessage, setLegalMessage] = useState(null);

  // Admin password change state
  const [credStatus, setCredStatus] = useState(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState(null);

  // Email deliverability test state
  const [testEmailTo, setTestEmailTo] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testEmailMessage, setTestEmailMessage] = useState(null);
  
  // Form values
  const [monthlyPrice, setMonthlyPrice] = useState('');
  const [monthlyStripeId, setMonthlyStripeId] = useState('');
  const [annualPrice, setAnnualPrice] = useState('');
  const [annualStripeId, setAnnualStripeId] = useState('');
  const [trialDays, setTrialDays] = useState('');

  useEffect(() => {
    checkAdminAccess();
    fetchPricing();
    fetchMonetization();
    fetchSiteConfig();
    fetchLegal();
    fetchCredStatus();
  }, []);

  // Sync legal form fields when active tab or legal data changes
  useEffect(() => {
    if (legal && legal[activeLegalTab]) {
      setLegalTitle(legal[activeLegalTab].title || '');
      setLegalContent(legal[activeLegalTab].content || '');
      setLegalEffectiveDate(legal[activeLegalTab].effective_date || '');
      setLegalMessage(null);
    }
  }, [activeLegalTab, legal]);

  const checkAdminAccess = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/me`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      
      if (response.ok) {
        const user = await response.json();
        if (!user.is_admin) {
          navigate('/');
        }
      } else {
        navigate('/admin/login');
      }
    } catch (error) {
      console.error('Admin check failed:', error);
      navigate('/admin/login');
    }
  };

  const fetchPricing = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/pricing`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setPricing(data);
        setMonthlyPrice(data.monthly.price.toString());
        setMonthlyStripeId(data.monthly.stripe_price_id || '');
        setAnnualPrice(data.annual.price.toString());
        setAnnualStripeId(data.annual.stripe_price_id || '');
        setTrialDays(data.trial.duration_days.toString());
      }
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMonetization = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/monetization`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setMonetization(data);
      }
    } catch (error) {
      console.error('Failed to fetch monetization:', error);
    }
  };

  const handleToggleMonetization = async () => {
    if (!monetization) return;
    const nextState = !monetization.enabled;

    const confirmMsg = nextState
      ? `Enable Freemium + Stripe?\n\nThis will:\n • Show pricing on Landing Page\n • Start trial countdowns for NEW signups\n • Auto-grandfather ${monetization.affected_users_on_enable || 0} existing trial/basic users (free forever)\n\nContinue?`
      : 'Disable Freemium + Stripe?\n\nAll users will have full free access. Pricing and upgrade banners will be hidden.\n\nContinue?';

    if (!window.confirm(confirmMsg)) return;

    setTogglingMonetization(true);
    setMonetizationMessage(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/monetization?enabled=${nextState}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setMonetizationMessage({ type: 'success', text: data.message });
        await fetchMonetization();
      } else {
        setMonetizationMessage({ type: 'error', text: 'Failed to update monetization setting' });
      }
    } catch (error) {
      console.error('Failed to toggle monetization:', error);
      setMonetizationMessage({ type: 'error', text: 'Failed to update monetization setting' });
    } finally {
      setTogglingMonetization(false);
    }
  };

  // --- Site config (copyright year, company name) ---
  const fetchSiteConfig = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/site-config`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setSiteConfig(data);
        setCopyrightYear((data.copyright_year ?? '').toString());
        setCompanyName(data.company_name || '');
        setTagline(data.tagline || '');
      }
    } catch (error) {
      console.error('Failed to fetch site config:', error);
    }
  };

  const handleSaveSiteConfig = async () => {
    setSavingSite(true);
    setSiteMessage(null);
    try {
      const year = parseInt(copyrightYear, 10);
      const body = {
        copyright_year: Number.isFinite(year) ? year : null,
        company_name: companyName,
        tagline,
      };
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/site-config`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
          body: JSON.stringify(body),
        }
      );
      if (response.ok) {
        setSiteMessage({ type: 'success', text: '✅ Site config updated. Applied instantly across all pages.' });
        await fetchSiteConfig();
      } else {
        setSiteMessage({ type: 'error', text: 'Failed to update site config' });
      }
    } catch (error) {
      console.error('Failed to save site config:', error);
      setSiteMessage({ type: 'error', text: 'Failed to update site config' });
    } finally {
      setSavingSite(false);
    }
  };

  // --- Legal documents (privacy + terms) ---
  const fetchLegal = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/legal`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      if (response.ok) {
        setLegal(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch legal documents:', error);
    }
  };

  const handleSaveLegal = async () => {
    setSavingLegal(true);
    setLegalMessage(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/legal/${activeLegalTab}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
          body: JSON.stringify({
            title: legalTitle,
            content: legalContent,
            effective_date: legalEffectiveDate,
          }),
        }
      );
      if (response.ok) {
        setLegalMessage({
          type: 'success',
          text: `✅ ${activeLegalTab === 'privacy' ? 'Privacy Policy' : 'Terms of Service'} updated.`,
        });
        await fetchLegal();
      } else {
        setLegalMessage({ type: 'error', text: 'Failed to update document' });
      }
    } catch (error) {
      console.error('Failed to save legal doc:', error);
      setLegalMessage({ type: 'error', text: 'Failed to update document' });
    } finally {
      setSavingLegal(false);
    }
  };

  // --- Admin password change ---
  const fetchCredStatus = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/credentials/status`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      if (response.ok) {
        setCredStatus(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch credential status:', error);
    }
  };

  const handleChangePassword = async () => {
    setPasswordMessage(null);
    if (newPassword.length < 12) {
      setPasswordMessage({ type: 'error', text: 'New password must be at least 12 characters long.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'New password and confirmation do not match.' });
      return;
    }
    if (newPassword === currentPassword) {
      setPasswordMessage({ type: 'error', text: 'New password must be different from the current one.' });
      return;
    }

    setChangingPassword(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/change-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
          body: JSON.stringify({
            current_password: currentPassword,
            new_password: newPassword,
          }),
        }
      );
      if (response.ok) {
        setPasswordMessage({
          type: 'success',
          text: '✅ Password updated. Use your new password on next login.',
        });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        await fetchCredStatus();
      } else {
        const err = await response.json().catch(() => ({}));
        setPasswordMessage({
          type: 'error',
          text: err.detail || 'Failed to change password',
        });
      }
    } catch (error) {
      console.error('Failed to change password:', error);
      setPasswordMessage({ type: 'error', text: 'Failed to change password' });
    } finally {
      setChangingPassword(false);
    }
  };

  // --- Deliverability: send a test email ---
  const handleSendTestEmail = async () => {
    setSendingTestEmail(true);
    setTestEmailMessage(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/send-test-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
          body: JSON.stringify({ to: testEmailTo.trim() || undefined }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setTestEmailMessage({
          type: 'success',
          text: data.message,
          note: data.note,
        });
      } else {
        setTestEmailMessage({
          type: 'error',
          text: data.detail || 'Failed to send test email',
        });
      }
    } catch (error) {
      console.error('Failed to send test email:', error);
      setTestEmailMessage({ type: 'error', text: 'Failed to send test email' });
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const params = new URLSearchParams();
      if (monthlyPrice !== pricing.monthly.price.toString()) {
        params.append('monthly_price', monthlyPrice);
      }
      if (monthlyStripeId !== (pricing.monthly.stripe_price_id || '')) {
        params.append('monthly_stripe_id', monthlyStripeId);
      }
      if (annualPrice !== pricing.annual.price.toString()) {
        params.append('annual_price', annualPrice);
      }
      if (annualStripeId !== (pricing.annual.stripe_price_id || '')) {
        params.append('annual_stripe_id', annualStripeId);
      }
      if (trialDays !== pricing.trial.duration_days.toString()) {
        params.append('trial_days', trialDays);
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/pricing/update?${params}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setMessage({
          type: 'success',
          text: data.message,
          note: data.note
        });
        fetchPricing();
      } else {
        setMessage({
          type: 'error',
          text: 'Failed to update pricing'
        });
      }
    } catch (error) {
      console.error('Failed to update pricing:', error);
      setMessage({
        type: 'error',
        text: 'Failed to update pricing'
      });
    } finally {
      setSaving(false);
    }
  };

  const calculateSavings = () => {
    const monthly = parseFloat(monthlyPrice) || 0;
    const annual = parseFloat(annualPrice) || 0;
    if (monthly > 0 && annual > 0) {
      const monthlyEquivalent = annual / 12;
      const savings = ((monthly - monthlyEquivalent) / monthly) * 100;
      return savings.toFixed(0);
    }
    return 0;
  };

  return (
    <div className="min-h-screen bg-brand-bg">
      {/* Header */}
      <div className="bg-brand-accent text-white shadow-lg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 hover:bg-brand-accent rounded-lg transition-colors"
                title="Back to Dashboard"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <div>
                <h1 className="text-3xl font-bold">Settings</h1>
                <p className="text-white/80 mt-1">Manage subscription pricing and trial period</p>
              </div>
            </div>
            <button
              onClick={fetchPricing}
              className="flex items-center space-x-2 px-3 py-2 bg-brand-accent hover:bg-brand-accent-hover rounded-lg transition-colors"
            >
              <RefreshCw className="h-5 w-5" />
              <span className="hidden md:inline">Refresh</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-accent mx-auto"></div>
          </div>
        ) : (
          <>
            {/* Monetization Master Switch */}
            <div
              data-testid="monetization-toggle-card"
              className={`rounded-xl p-6 mb-6 border-2 shadow-sm ${
                monetization?.enabled
                  ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300'
                  : 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-300'
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start space-x-3 flex-1">
                  <Power
                    className={`h-7 w-7 mt-0.5 ${
                      monetization?.enabled ? 'text-green-600' : 'text-gray-400'
                    }`}
                  />
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 text-lg">
                      Monetization (Freemium + Stripe)
                    </h3>
                    <p
                      className={`text-sm font-semibold mt-1 ${
                        monetization?.enabled ? 'text-green-700' : 'text-gray-600'
                      }`}
                    >
                      Currently:{' '}
                      {monetization?.enabled ? 'ENABLED' : 'DISABLED (all users free)'}
                    </p>
                    <p className="text-sm text-gray-700 mt-2">
                      {monetization?.enabled ? (
                        <>
                          Freemium flow is live. New signups start a{' '}
                          <strong>{pricing?.trial?.duration_days || 14}-day Premium trial</strong>,
                          then convert to Basic or Premium. Pricing is shown on the landing
                          page and upgrade banners.
                        </>
                      ) : (
                        <>
                          All users currently have <strong>full free access</strong>. Pricing,
                          trial countdowns, and upgrade prompts are hidden across the app.
                          New signups are automatically grandfathered.
                        </>
                      )}
                    </p>
                    {!monetization?.enabled && monetization && (
                      <p className="text-xs text-gray-500 mt-2">
                        {monetization.total_users} total user
                        {monetization.total_users === 1 ? '' : 's'} in the system. Enabling will
                        grandfather{' '}
                        <strong>{monetization.affected_users_on_enable || 0}</strong>{' '}
                        existing trial/basic user
                        {monetization.affected_users_on_enable === 1 ? '' : 's'} so they keep
                        free access forever.
                      </p>
                    )}
                  </div>
                </div>
                <button
                  data-testid="monetization-toggle-btn"
                  onClick={handleToggleMonetization}
                  disabled={togglingMonetization || !monetization}
                  className={`shrink-0 px-5 py-3 rounded-lg font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                    monetization?.enabled
                      ? 'bg-red-500 hover:bg-red-600'
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {togglingMonetization
                    ? 'Updating…'
                    : monetization?.enabled
                    ? 'Disable'
                    : 'Enable Monetization'}
                </button>
              </div>

              {monetizationMessage && (
                <div
                  className={`mt-4 p-3 rounded-lg text-sm ${
                    monetizationMessage.type === 'success'
                      ? 'bg-green-100 text-green-900 border border-green-200'
                      : 'bg-red-100 text-red-900 border border-red-200'
                  }`}
                >
                  {monetizationMessage.text}
                </div>
              )}
            </div>

            {/* Site Config: copyright year, company name */}
            <div
              data-testid="site-config-card"
              className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-100"
            >
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Calendar className="h-6 w-6 mr-2 text-brand-accent" />
                Site Configuration
              </h2>
              <p className="text-sm text-gray-600 mb-5">
                These values are shown in footers across the public and app pages.
                Changes take effect immediately everywhere.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Copyright Year
                  </label>
                  <input
                    data-testid="copyright-year-input"
                    type="number"
                    min="2000"
                    max="2100"
                    value={copyrightYear}
                    onChange={(e) => setCopyrightYear(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-transparent"
                    placeholder="2026"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Current: {siteConfig?.copyright_year}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    data-testid="company-name-input"
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-transparent"
                    placeholder="ReflectiveMind"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Footer Tagline
                  </label>
                  <input
                    data-testid="tagline-input"
                    type="text"
                    value={tagline}
                    onChange={(e) => setTagline(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-transparent"
                    placeholder="Empowering growth through reflection."
                  />
                </div>
              </div>

              {siteMessage && (
                <div
                  className={`mt-4 p-3 rounded-lg text-sm ${
                    siteMessage.type === 'success'
                      ? 'bg-green-100 text-green-900 border border-green-200'
                      : 'bg-red-100 text-red-900 border border-red-200'
                  }`}
                >
                  {siteMessage.text}
                </div>
              )}

              <div className="mt-5 flex items-center gap-3">
                <button
                  data-testid="save-site-config-btn"
                  onClick={handleSaveSiteConfig}
                  disabled={savingSite}
                  className="bg-brand-cta hover:bg-brand-cta-hover text-white py-2.5 px-6 rounded-lg font-semibold disabled:opacity-50 flex items-center gap-2"
                >
                  {savingSite ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" /> Saving…
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" /> Save Site Config
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-500">
                  Preview: <span className="font-mono">© {copyrightYear || '—'} {companyName || '—'}. {tagline}</span>
                </p>
              </div>
            </div>

            {/* Lite invite codes (org pilots) */}
            <AdminOrgInvites />

            {/* Legal Documents Editor */}
            <div
              data-testid="legal-editor-card"
              className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-100"
            >
              <h2 className="text-xl font-bold text-gray-900 mb-1 flex items-center">
                <FileText className="h-6 w-6 mr-2 text-brand-accent" />
                Legal Documents
              </h2>
              <p className="text-sm text-gray-600 mb-5">
                Edit Privacy Policy and Terms of Service. Content is written in{' '}
                <a
                  href="https://commonmark.org/help/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand-accent underline"
                >
                  Markdown
                </a>
                . Published instantly at{' '}
                <code className="bg-gray-100 px-1 rounded">/privacy</code> and{' '}
                <code className="bg-gray-100 px-1 rounded">/terms</code>.
              </p>

              <div className="flex gap-2 mb-4 border-b border-gray-200">
                {[
                  { key: 'privacy', label: 'Privacy Policy' },
                  { key: 'terms', label: 'Terms of Service' },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    data-testid={`legal-tab-${tab.key}`}
                    onClick={() => setActiveLegalTab(tab.key)}
                    className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
                      activeLegalTab === tab.key
                        ? 'border-brand-accent text-brand-accent'
                        : 'border-transparent text-gray-500 hover:text-gray-800'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Document Title
                  </label>
                  <input
                    data-testid="legal-title-input"
                    type="text"
                    value={legalTitle}
                    onChange={(e) => setLegalTitle(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-transparent"
                    placeholder="Privacy Policy"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Effective Date
                  </label>
                  <input
                    data-testid="legal-date-input"
                    type="date"
                    value={legalEffectiveDate}
                    onChange={(e) => setLegalEffectiveDate(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-transparent"
                  />
                </div>
              </div>

              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content (Markdown)
              </label>
              <textarea
                data-testid="legal-content-input"
                value={legalContent}
                onChange={(e) => setLegalContent(e.target.value)}
                rows={20}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-transparent font-mono text-sm"
                placeholder="# Heading...&#10;Write your policy in Markdown."
              />

              {legalMessage && (
                <div
                  className={`mt-4 p-3 rounded-lg text-sm ${
                    legalMessage.type === 'success'
                      ? 'bg-green-100 text-green-900 border border-green-200'
                      : 'bg-red-100 text-red-900 border border-red-200'
                  }`}
                >
                  {legalMessage.text}
                </div>
              )}

              <div className="mt-5 flex items-center gap-3">
                <button
                  data-testid="save-legal-btn"
                  onClick={handleSaveLegal}
                  disabled={savingLegal}
                  className="bg-brand-cta hover:bg-brand-cta-hover text-white py-2.5 px-6 rounded-lg font-semibold disabled:opacity-50 flex items-center gap-2"
                >
                  {savingLegal ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" /> Saving…
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />{' '}
                      Save {activeLegalTab === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
                    </>
                  )}
                </button>
                <a
                  href={`/${activeLegalTab}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-brand-accent hover:underline"
                >
                  Preview at /{activeLegalTab} ↗
                </a>
              </div>
            </div>

            {/* Admin Password Change */}
            <div
              data-testid="admin-password-card"
              className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-100"
            >
              <h2 className="text-xl font-bold text-gray-900 mb-1 flex items-center">
                <KeyRound className="h-6 w-6 mr-2 text-brand-accent" />
                Admin Account Security
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                {credStatus?.admin_email && (
                  <>
                    Signed in as <strong>{credStatus.admin_email}</strong>.{' '}
                  </>
                )}
                Rotate your admin password any time — no server restart needed.
                The new password is stored as a bcrypt hash in the database and
                takes precedence over the one in <code className="bg-gray-100 px-1 rounded">.env</code>.
              </p>

              {credStatus?.using_env_password && (
                <div
                  data-testid="admin-password-warning"
                  className="flex items-start space-x-3 p-3 mb-4 rounded-lg bg-amber-50 border border-amber-200"
                >
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <p className="text-sm text-brand-ink">
                    <strong>Heads up:</strong> you're still using the password from{' '}
                    <code className="bg-amber-100 px-1 rounded">.env</code>. It's strongly
                    recommended to rotate it before going live.
                  </p>
                </div>
              )}

              {credStatus?.password_updated_at && !credStatus.using_env_password && (
                <p className="text-xs text-gray-500 mb-4">
                  Last changed: {new Date(credStatus.password_updated_at).toLocaleString()}
                </p>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Password
                  </label>
                  <input
                    data-testid="current-password-input"
                    type="password"
                    autoComplete="current-password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-transparent"
                    placeholder="••••••••••"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <input
                    data-testid="new-password-input"
                    type="password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-transparent"
                    placeholder="At least 12 characters"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password
                  </label>
                  <input
                    data-testid="confirm-password-input"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-transparent"
                    placeholder="Repeat new password"
                  />
                </div>
              </div>

              {passwordMessage && (
                <div
                  data-testid="admin-password-message"
                  className={`mt-4 p-3 rounded-lg text-sm ${
                    passwordMessage.type === 'success'
                      ? 'bg-green-100 text-green-900 border border-green-200'
                      : 'bg-red-100 text-red-900 border border-red-200'
                  }`}
                >
                  {passwordMessage.text}
                </div>
              )}

              <div className="mt-5">
                <button
                  data-testid="change-password-btn"
                  onClick={handleChangePassword}
                  disabled={
                    changingPassword ||
                    !currentPassword ||
                    !newPassword ||
                    !confirmPassword
                  }
                  className="bg-brand-cta hover:bg-brand-cta-hover text-white py-2.5 px-6 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {changingPassword ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" /> Updating…
                    </>
                  ) : (
                    <>
                      <KeyRound className="h-4 w-4" /> Change Admin Password
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Email deliverability test */}
            <div
              data-testid="test-email-card"
              className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-100"
            >
              <h2 className="text-xl font-bold text-gray-900 mb-1 flex items-center">
                <MailCheck className="h-6 w-6 mr-2 text-brand-accent" />
                Email Deliverability Test
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Send a small test email through your SMTP provider to confirm
                password-reset and transactional emails will actually reach the
                inbox. Leave blank to send to the admin email from{' '}
                <code className="bg-gray-100 px-1 rounded">backend/.env</code>.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Send to (optional)
                  </label>
                  <input
                    data-testid="test-email-to-input"
                    type="email"
                    value={testEmailTo}
                    onChange={(e) => setTestEmailTo(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-transparent"
                    placeholder={credStatus?.admin_email || 'admin@example.com'}
                  />
                </div>
                <div>
                  <button
                    data-testid="send-test-email-btn"
                    onClick={handleSendTestEmail}
                    disabled={sendingTestEmail}
                    className="w-full bg-brand-cta hover:bg-brand-cta-hover text-white py-3 px-4 rounded-lg font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {sendingTestEmail ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" /> Sending…
                      </>
                    ) : (
                      <>
                        <MailCheck className="h-4 w-4" /> Send test email
                      </>
                    )}
                  </button>
                </div>
              </div>

              {testEmailMessage && (
                <div
                  data-testid="test-email-result"
                  className={`mt-4 p-3 rounded-lg text-sm ${
                    testEmailMessage.type === 'success'
                      ? 'bg-green-100 text-green-900 border border-green-200'
                      : 'bg-red-100 text-red-900 border border-red-200'
                  }`}
                >
                  <p className="font-semibold">{testEmailMessage.text}</p>
                  {testEmailMessage.note && (
                    <p className="text-xs mt-1 opacity-80">{testEmailMessage.note}</p>
                  )}
                </div>
              )}
            </div>

            {/* Important Notice */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-8">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-6 w-6 text-green-600 mt-0.5" />
                <div>
                  <h3 className="font-bold text-green-900 mb-2">✨ Instant Updates - No Restart Required!</h3>
                  <ul className="text-sm text-green-800 space-y-1 list-disc ml-5">
                    <li>All changes take effect <strong>immediately</strong> - no backend restart needed!</li>
                    <li>Pricing changes apply to <strong>new subscriptions only</strong></li>
                    <li>Existing subscribers keep their current pricing</li>
                    <li>Stripe Price IDs are stored in database - update anytime without code changes</li>
                    <li>Landing page and upgrade modals automatically fetch new prices</li>
                    <li>Leave Price ID blank to use dynamic pricing (Stripe creates prices on-the-fly)</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Message */}
            {message && (
              <div className={`mb-6 p-4 rounded-lg border ${
                message.type === 'success' 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <p className={`font-semibold ${
                  message.type === 'success' ? 'text-green-900' : 'text-red-900'
                }`}>
                  {message.text}
                </p>
                {message.note && (
                  <p className="text-sm text-gray-700 mt-2">{message.note}</p>
                )}
              </div>
            )}

            {/* Current Pricing Overview */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Current Pricing</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-brand-accent/5 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Monthly</p>
                  <p className="text-3xl font-bold text-brand-accent">{pricing?.monthly?.formatted}</p>
                  <p className="text-xs text-gray-500 mt-1">per month</p>
                </div>
                <div className="text-center p-4 bg-brand-accent/5 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Annual</p>
                  <p className="text-3xl font-bold text-brand-accent">{pricing?.annual?.formatted}</p>
                  <p className="text-xs text-gray-500 mt-1">per year</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Trial Period</p>
                  <p className="text-3xl font-bold text-green-600">{pricing?.trial?.duration_days}</p>
                  <p className="text-xs text-gray-500 mt-1">days</p>
                </div>
              </div>
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                <p className="text-sm text-yellow-800">
                  <strong>Annual Savings:</strong> {pricing?.annual?.savings_percentage}% 
                  (£{pricing?.annual?.monthly_equivalent?.toFixed(2)}/month)
                </p>
              </div>
            </div>

            {/* Update Form */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <DollarSign className="h-6 w-6 mr-2 text-brand-accent" />
                Update Pricing
              </h2>

              <div className="space-y-6">
                {/* Monthly Price */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Monthly Subscription Price (£)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={monthlyPrice}
                      onChange={(e) => setMonthlyPrice(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-transparent"
                      placeholder="1.00"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Current: {pricing?.monthly?.formatted} per month
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Monthly Stripe Price ID
                    </label>
                    <input
                      type="text"
                      value={monthlyStripeId}
                      onChange={(e) => setMonthlyStripeId(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-transparent font-mono text-sm"
                      placeholder="price_1ABC123..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {pricing?.monthly?.stripe_price_id ? (
                        <span>Current: {pricing.monthly.stripe_price_id.substring(0, 20)}...</span>
                      ) : (
                        <span className="text-amber-600">⚠️ No Price ID set (using dynamic pricing)</span>
                      )}
                    </p>
                  </div>
                </div>

                {/* Annual Price */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Annual Subscription Price (£)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={annualPrice}
                      onChange={(e) => setAnnualPrice(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-transparent"
                      placeholder="10.00"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Current: {pricing?.annual?.formatted} per year • 
                      New savings: {calculateSavings()}% • 
                      £{(parseFloat(annualPrice || 0) / 12).toFixed(2)}/month equivalent
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Annual Stripe Price ID
                    </label>
                    <input
                      type="text"
                      value={annualStripeId}
                      onChange={(e) => setAnnualStripeId(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-transparent font-mono text-sm"
                      placeholder="price_1XYZ456..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {pricing?.annual?.stripe_price_id ? (
                        <span>Current: {pricing.annual.stripe_price_id.substring(0, 20)}...</span>
                      ) : (
                        <span className="text-amber-600">⚠️ No Price ID set (using dynamic pricing)</span>
                      )}
                    </p>
                  </div>
                </div>

                {/* Trial Days */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Trial Period (Days)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={trialDays}
                    onChange={(e) => setTrialDays(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-transparent"
                    placeholder="14"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Current: {pricing?.trial?.duration_days} days of Premium trial
                  </p>
                </div>

                {/* Save Button */}
                <div className="flex items-center space-x-3 pt-4">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex-1 bg-brand-cta hover:bg-brand-cta-hover text-white py-3 px-6 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {saving ? (
                      <>
                        <RefreshCw className="h-5 w-5 animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save className="h-5 w-5" />
                        <span>Save Changes</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setMonthlyPrice(pricing.monthly.price.toString());
                      setMonthlyStripeId(pricing.monthly.stripe_price_id || '');
                      setAnnualPrice(pricing.annual.price.toString());
                      setAnnualStripeId(pricing.annual.stripe_price_id || '');
                      setTrialDays(pricing.trial.duration_days.toString());
                      setMessage(null);
                    }}
                    disabled={saving}
                    className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>

            {/* Stripe Integration Notice */}
            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
              <h3 className="font-bold text-blue-900 mb-3">Stripe Integration Steps:</h3>
              <ol className="text-sm text-blue-800 space-y-2 list-decimal ml-5">
                <li>Create new Price IDs in your Stripe Dashboard for each subscription interval</li>
                <li>Update <code className="bg-blue-100 px-1 py-0.5 rounded">/app/backend/pricing_config.py</code> with the new Stripe Price IDs:
                  <pre className="bg-blue-100 p-2 rounded mt-1 text-xs overflow-x-auto">
{`PRICING = {
  "monthly": {
    "stripe_price_id": "price_xxx",  # Your Stripe Price ID
  },
  "annual": {
    "stripe_price_id": "price_yyy",  # Your Stripe Price ID
  }
}`}
                  </pre>
                </li>
                <li>Restart the backend: <code className="bg-blue-100 px-1 py-0.5 rounded">sudo supervisorctl restart backend</code></li>
                <li>Test the checkout flow with Stripe test cards</li>
              </ol>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminSettings;
